import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';

// Global scroll-to-top on route change
import WelcomeGuide from './WelcomeGuide';
import { Tutorial, useTutorial } from './Tutorial';
import { clearToken, getToken, apiGet } from '@/utils/api';
import { AshChatButton } from './AshChat';
import { StreakCelebration } from './Celebrations';
import { AnimatedFlames, ShareableMilestoneCard, useMilestoneShare } from './MilestoneShare';
import InstallPrompt from './InstallPrompt';
import { NotificationPrompt, LoginNotificationPrompt } from './NotificationSettings';
import { NotificationBell, InvitationModal } from './NotificationBell';
import { registerServiceWorker, updateUserTimezone } from '@/utils/notifications';
import { RealtimeToastContainer } from './RealtimeToast';
import PushNotificationPrompt from './PushNotificationPrompt';
import { onForegroundMessage } from '@/utils/pushNotifications';
import { 
  LayoutDashboard, 
  DollarSign, 
  Receipt, 
  Target, 
  Flame, 
  Trophy, 
  Mail, 
  Users, 
  Snowflake, 
  Lightbulb, 
  History, 
  User, 
  Settings,
  LogOut,
  Sparkles,
  Shield
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Street Cred title and styling based on streak
const getStreetCredInfo = (streakData) => {
  const { current_streak, streak_level, street_cred_title, street_cred_emoji, guap_meter } = streakData;
  
  if (current_streak === 0) {
    return { 
      emoji: '💸', 
      title: null, 
      color: 'text-gray-400', 
      bg: 'bg-gray-100',
      borderColor: 'border-gray-200',
      glowColor: null,
      meterColor: 'bg-blue-400' // Cold
    };
  }
  
  // Map streak levels to visual styles
  const styles = {
    grey: { 
      emoji: street_cred_emoji || '🔥', 
      color: 'text-gray-500', 
      bg: 'bg-gray-200',
      borderColor: 'border-gray-300',
      glowColor: null,
      meterColor: 'bg-blue-400' // Cold
    },
    orange: { 
      emoji: street_cred_emoji || '📄', 
      color: 'text-orange-500', 
      bg: 'bg-orange-100',
      borderColor: 'border-orange-300',
      glowColor: 'shadow-orange-500/30',
      meterColor: 'bg-orange-400' // Warming up
    },
    gold: { 
      emoji: street_cred_emoji || '💰', 
      color: 'text-yellow-500', 
      bg: 'bg-yellow-100',
      borderColor: 'border-yellow-400',
      glowColor: 'shadow-yellow-500/40',
      meterColor: 'bg-yellow-500' // Heating up
    },
    fire: { 
      emoji: street_cred_emoji || '🔥', 
      color: 'text-red-500', 
      bg: 'bg-gradient-to-r from-orange-100 to-red-100',
      borderColor: 'border-red-400',
      glowColor: 'shadow-red-500/50',
      meterColor: 'bg-gradient-to-r from-orange-500 to-red-500' // On fire
    },
    mogul: { 
      emoji: street_cred_emoji || '👑', 
      color: 'text-purple-600', 
      bg: 'bg-gradient-to-r from-purple-100 to-pink-100',
      borderColor: 'border-purple-400',
      glowColor: 'shadow-purple-500/50',
      meterColor: 'bg-gradient-to-r from-purple-500 to-pink-500' // Mogul status
    }
  };
  
  const style = styles[streak_level] || styles.grey;
  
  return {
    ...style,
    title: street_cred_title,
    emoji: street_cred_emoji || style.emoji
  };
};

function Layout({ children, user }) {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Global scroll-to-top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showWelcomeGuide, setShowWelcomeGuide] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [streak, setStreak] = useState({ 
    current_streak: 0, 
    streak_level: 'none', 
    today_logged: false,
    street_cred_title: null,
    street_cred_emoji: '💸',
    guap_meter: 'cold',
    bounce_backs: 0,
    has_bounce_back: false
  });
  const [showStreakTooltip, setShowStreakTooltip] = useState(false);
  const [previousStreak, setPreviousStreak] = useState(0);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [showLoginNotificationPrompt, setShowLoginNotificationPrompt] = useState(false);
  const { showTutorial, setShowTutorial, completeTutorial, resetTutorial } = useTutorial();
  
  // Milestone share hook
  const { showMilestone, milestoneData, triggerMilestone, closeMilestone } = useMilestoneShare();

  // Register service worker and update timezone on mount
  useEffect(() => {
    registerServiceWorker();
    const token = getToken();
    if (token) {
      updateUserTimezone(token);
    }
  }, []);

  // Fetch subscription status
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await apiGet('/subscription/status');
        if (response.ok) {
          const data = await response.json();
          setIsSubscribed(data.status === 'active' || data.lifetime_free === true);
        }
      } catch (error) {
        console.error('Failed to fetch subscription:', error);
      }
    };
    if (user) {
      fetchSubscription();
    }
  }, [user]);

  // Check if user is admin
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const response = await apiGet('/admin/stats');
        if (response.ok) {
          setIsAdmin(true);
        }
      } catch (error) {
        // Not admin or error - keep isAdmin false
      }
    };
    if (user) {
      checkAdmin();
    }
  }, [user]);

  // Fetch streak data
  const fetchStreak = async () => {
    try {
      const response = await apiGet('/streaks');
      if (response.ok) {
        const data = await response.json();
        // Track previous streak for milestone celebration
        setPreviousStreak(streak.current_streak);
        setStreak(data);
      }
    } catch (error) {
      console.error('Failed to fetch streak:', error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchStreak();
      // Refresh streak every 30 seconds while user is active
      const interval = setInterval(fetchStreak, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Also refresh streak when window gets focus (user comes back to tab)
  useEffect(() => {
    const handleFocus = () => {
      if (user) {
        fetchStreak();
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user]);

  // Check if user should see welcome guide
  useEffect(() => {
    if (user) {
      const hasSeenGuide = localStorage.getItem(`welcomeGuide_${user.user_id}`);
      if (!hasSeenGuide) {
        setShowWelcomeGuide(true);
      }
    }
  }, [user]);

  // Show notification prompt after user has been active (installed as PWA)
  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone;
    
    if (isStandalone && user) {
      const hasDismissedPrompt = localStorage.getItem(`notificationPrompt_${user.user_id}`);
      if (!hasDismissedPrompt) {
        // Show after a delay
        const timer = setTimeout(() => {
          setShowNotificationPrompt(true);
        }, 5000);
        return () => clearTimeout(timer);
      }
    }
  }, [user]);

  // Setup FCM foreground message handler
  useEffect(() => {
    const unsubscribe = onForegroundMessage((payload) => {
      console.log('FCM foreground notification:', payload);
      // Could also show a toast notification here
    });
    return unsubscribe;
  }, []);

  const dismissNotificationPrompt = () => {
    if (user) {
      localStorage.setItem(`notificationPrompt_${user.user_id}`, 'dismissed');
    }
    setShowNotificationPrompt(false);
  };

  // Show login notification prompt on each login (recurring)
  useEffect(() => {
    if (!user?.user_id) return;
    
    // Check if user permanently dismissed it
    const neverAsk = localStorage.getItem(`loginNotificationPrompt_${user.user_id}_never`);
    if (neverAsk === 'true') return;
    
    // Check last dismissal time - show again after 24 hours
    const lastDismissed = localStorage.getItem(`loginNotificationPrompt_${user.user_id}`);
    if (lastDismissed) {
      const hoursSinceDismissal = (Date.now() - parseInt(lastDismissed)) / (1000 * 60 * 60);
      if (hoursSinceDismissal < 24) return; // Don't show if dismissed within 24 hours
    }
    
    // Show after a short delay (after welcome guide if shown)
    const timer = setTimeout(() => {
      // Don't show if welcome guide or tutorial is active
      if (!showWelcomeGuide && !showTutorial) {
        setShowLoginNotificationPrompt(true);
      }
    }, 3000);
    
    return () => clearTimeout(timer);
  }, [user?.user_id, showWelcomeGuide, showTutorial]);

  const dismissLoginNotificationPrompt = () => {
    setShowLoginNotificationPrompt(false);
  };

  const dismissWelcomeGuide = () => {
    if (user) {
      localStorage.setItem(`welcomeGuide_${user.user_id}`, 'true');
    }
    setShowWelcomeGuide(false);
  };

  const handleLogout = async () => {
    try {
      // Add Authorization header for mobile browsers
      const token = getToken();
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers,
      });
      // Clear stored token
      clearToken();
      navigate('/app/login');
    } catch (error) {
      console.error('Logout error:', error);
      clearToken();
      navigate('/app/login');
    }
  };

  const navItems = [
    { path: '/app/dashboard', label: 'Dashboard', icon: LayoutDashboard, tutorialId: 'dashboard-nav' },
    { path: '/app/income', label: 'Income', icon: DollarSign, tutorialId: 'income-nav' },
    { path: '/app/bills', label: 'Bills', icon: Receipt, tutorialId: 'bills-nav' },
    { path: '/app/goals', label: 'Goals', icon: Target, tutorialId: 'goals-nav' },
    { path: '/app/challenges', label: 'Challenges', icon: Flame, tutorialId: 'challenges-nav' },
    { path: '/app/badges', label: 'Badges', icon: Trophy, tutorialId: 'badges-nav' },
    { path: '/app/envelope-v2', label: 'Envelopes', icon: Mail, tutorialId: 'envelope-nav' },
    { path: '/app/household', label: 'Squad', icon: Users, tutorialId: 'family-nav' },
    { path: '/app/debt-snowball', label: 'Debt', icon: Snowflake, tutorialId: 'debt-nav' },
    { path: '/app/quick-wins', label: 'Tips', icon: Lightbulb, tutorialId: 'quickwins-nav' },
    { path: '/app/history', label: 'History', icon: History, tutorialId: 'history-nav' },
    { path: '/app/contact', label: 'Contact', icon: null, tutorialId: 'contact-nav' },
    { path: '/app/about', label: 'About', icon: null, tutorialId: 'about-nav' },
  ];

  // Grouped nav items for mobile menu
  const mobileNavGroups = {
    core: {
      label: 'CORE',
      items: [
        { path: '/app/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { path: '/app/income', label: 'Income', icon: DollarSign },
        { path: '/app/bills', label: 'Bills', icon: Receipt },
        { path: '/app/debt-snowball', label: 'Debt', icon: Snowflake },
        { path: '/app/envelope-v2', label: 'Envelopes', icon: Mail },
      ]
    },
    growth: {
      label: 'GROWTH & COMMUNITY',
      items: [
        { path: '/app/goals', label: 'Goals', icon: Target },
        { path: '/app/challenges', label: 'Challenges', icon: Flame },
        { path: '/app/badges', label: 'Badges', icon: Trophy },
        { path: '/app/quick-wins', label: 'Tips', icon: Lightbulb },
        { path: '/app/household', label: 'Squad', icon: Users },
      ]
    },
    system: {
      label: 'SYSTEM',
      items: [
        { path: '/app/history', label: 'History', icon: History },
        { path: '/app/profile', label: 'Profile & Settings', icon: Settings },
      ]
    }
  };

  // When tutorial is active, show full navigation so all items can be highlighted
  const showFullNav = showTutorial && !showWelcomeGuide;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Skip Link for Accessibility */}
      <a 
        href="#main-content" 
        className="skip-link"
        data-testid="skip-link"
      >
        Skip to main content
      </a>
      
      {/* Streak Milestone Celebration */}
      <StreakCelebration 
        streakCount={streak.current_streak}
        previousStreak={previousStreak}
      />
      
      {/* Welcome Guide for new users */}
      {showWelcomeGuide && (
        <WelcomeGuide user={user} onDismiss={dismissWelcomeGuide} />
      )}
      
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/app" className="flex items-center gap-2 flex-shrink-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_budget-buddy-4768/artifacts/uj88omcr_ChatGPT%20Image%20Feb%2011%2C%202026%2C%2009_23_14%20PM.png" 
                alt="GuapGoals" 
                className="h-10 w-auto"
              />
            </Link>

            {/* Desktop Navigation - Dropdown Menu */}
            <nav className="hidden md:flex items-center space-x-1">
              {/* Core Navigation Links - Always visible */}
              <Link
                to="/app/dashboard"
                data-testid="nav-dashboard"
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  location.pathname === '/app/dashboard'
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <LayoutDashboard className="w-4 h-4" />
                <span>Dashboard</span>
              </Link>
              
              {/* Money Dropdown */}
              <div className="relative group">
                <button
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
                    ['/app/income', '/app/bills', '/app/debt-snowball', '/app/envelope-v2'].includes(location.pathname)
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                  aria-haspopup="true"
                  aria-expanded="false"
                  aria-label="Money menu"
                >
                  <DollarSign className="w-4 h-4" aria-hidden="true" />
                  <span>Money</span>
                  <svg className="w-3 h-3 ml-1 group-hover:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div className="absolute left-0 mt-1 w-48 bg-white rounded-xl shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50" role="menu" aria-label="Money submenu">
                  <div className="py-2">
                    <Link to="/app/income" role="menuitem" className={`flex items-center gap-3 px-4 py-2.5 text-sm focus:outline-none focus:bg-blue-50 ${location.pathname === '/app/income' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`}>
                      <DollarSign className="w-4 h-4" aria-hidden="true" /> Income
                    </Link>
                    <Link to="/app/bills" role="menuitem" className={`flex items-center gap-3 px-4 py-2.5 text-sm focus:outline-none focus:bg-blue-50 ${location.pathname === '/app/bills' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`}>
                      <Receipt className="w-4 h-4" aria-hidden="true" /> Bills
                    </Link>
                    <Link to="/app/debt-snowball" role="menuitem" className={`flex items-center gap-3 px-4 py-2.5 text-sm focus:outline-none focus:bg-blue-50 ${location.pathname === '/app/debt-snowball' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`}>
                      <Snowflake className="w-4 h-4" aria-hidden="true" /> Debt Snowball
                    </Link>
                    <Link to="/app/envelope-v2" role="menuitem" className={`flex items-center gap-3 px-4 py-2.5 text-sm focus:outline-none focus:bg-blue-50 ${location.pathname === '/app/envelope-v2' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`}>
                      <Mail className="w-4 h-4" aria-hidden="true" /> Envelopes
                    </Link>
                  </div>
                </div>
              </div>
              
              {/* Goals & Growth Dropdown */}
              <div className="relative group">
                <button
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
                    ['/app/goals', '/app/challenges', '/app/badges', '/app/quick-wins'].includes(location.pathname)
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                  aria-haspopup="true"
                  aria-expanded="false"
                  aria-label="Goals menu"
                >
                  <Target className="w-4 h-4" aria-hidden="true" />
                  <span>Goals</span>
                  <svg className="w-3 h-3 ml-1 group-hover:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div className="absolute left-0 mt-1 w-48 bg-white rounded-xl shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50" role="menu" aria-label="Goals submenu">
                  <div className="py-2">
                    <Link to="/app/goals" role="menuitem" className={`flex items-center gap-3 px-4 py-2.5 text-sm focus:outline-none focus:bg-blue-50 ${location.pathname === '/app/goals' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`}>
                      <Target className="w-4 h-4" aria-hidden="true" /> Savings Goals
                    </Link>
                    <Link to="/app/challenges" role="menuitem" className={`flex items-center gap-3 px-4 py-2.5 text-sm focus:outline-none focus:bg-blue-50 ${location.pathname === '/app/challenges' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`}>
                      <Flame className="w-4 h-4" aria-hidden="true" /> Challenges
                    </Link>
                    <Link to="/app/badges" role="menuitem" className={`flex items-center gap-3 px-4 py-2.5 text-sm focus:outline-none focus:bg-blue-50 ${location.pathname === '/app/badges' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`}>
                      <Trophy className="w-4 h-4" aria-hidden="true" /> Badges
                    </Link>
                    <Link to="/app/quick-wins" role="menuitem" className={`flex items-center gap-3 px-4 py-2.5 text-sm focus:outline-none focus:bg-blue-50 ${location.pathname === '/app/quick-wins' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`}>
                      <Lightbulb className="w-4 h-4" aria-hidden="true" /> Tips & Quick Wins
                    </Link>
                  </div>
                </div>
              </div>
              
              {/* Squad */}
              <Link
                to="/app/household"
                data-testid="nav-squad"
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  location.pathname === '/app/household'
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <Users className="w-4 h-4" />
                <span>Squad</span>
              </Link>
              
              {/* History */}
              <Link
                to="/app/history"
                data-testid="nav-history"
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  location.pathname === '/app/history'
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <History className="w-4 h-4" />
                <span>History</span>
              </Link>
            </nav>

            {/* User Menu */}
            <div className="flex items-center gap-3">
              {/* Street Cred / Streak Indicator */}
              <div 
                className="relative"
                onMouseEnter={() => setShowStreakTooltip(true)}
                onMouseLeave={() => setShowStreakTooltip(false)}
                onClick={() => {
                  setShowStreakTooltip(!showStreakTooltip);
                  // Refresh streak data when opening tooltip
                  if (!showStreakTooltip) {
                    fetchStreak();
                  }
                }}
              >
                <div 
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 cursor-pointer transition-all ${getStreetCredInfo(streak).bg} ${getStreetCredInfo(streak).borderColor} ${getStreetCredInfo(streak).glowColor ? `shadow-lg ${getStreetCredInfo(streak).glowColor}` : ''}`}
                  data-testid="streak-indicator"
                >
                  {/* Animated flames for 7+ day streaks */}
                  {streak.current_streak >= 7 && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 pointer-events-none">
                      <AnimatedFlames streakDays={streak.current_streak} />
                    </div>
                  )}
                  <span className={`text-lg ${getStreetCredInfo(streak).color}`}>
                    {getStreetCredInfo(streak).emoji}
                  </span>
                  <span className={`font-bold text-sm ${streak.current_streak > 0 ? 'text-gray-800' : 'text-gray-500'}`}>
                    {streak.current_streak}
                  </span>
                  {streak.street_cred_title && (
                    <span className="hidden lg:inline text-xs font-semibold text-gray-600 ml-1">
                      {streak.street_cred_title}
                    </span>
                  )}
                </div>
                
                {/* Streak Tooltip */}
                {showStreakTooltip && (
                  <>
                    {/* Mobile backdrop */}
                    <div 
                      className="fixed inset-0 bg-black/20 z-40 sm:hidden"
                      onClick={() => setShowStreakTooltip(false)}
                    />
                    <div className="fixed sm:absolute top-20 sm:top-full left-4 right-4 sm:left-auto sm:right-0 sm:mt-2 sm:w-72 bg-white rounded-xl shadow-xl border border-gray-200 p-4 z-50">
                      {/* Close button for mobile */}
                      <button 
                        className="absolute top-2 right-2 sm:hidden text-gray-400 hover:text-gray-600 text-xl"
                        onClick={() => setShowStreakTooltip(false)}
                      >
                        ✕
                      </button>
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-3xl">{getStreetCredInfo(streak).emoji}</span>
                        <div>
                          <p className="font-bold text-gray-900">
                            {streak.street_cred_title || 'Build Your Streak'}
                          </p>
                          <p className="text-sm text-gray-500">{streak.current_streak} day streak</p>
                        </div>
                      </div>
                      
                      {/* Guap Meter */}
                      <div className="mb-3">
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>Guap Meter</span>
                          <span className="capitalize">{streak.guap_meter?.replace('_', ' ') || 'Cold'}</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                          className={`h-full transition-all duration-500 ${getStreetCredInfo(streak).meterColor}`}
                          style={{ width: `${Math.min(100, (streak.current_streak / 30) * 100)}%` }}
                        />
                      </div>
                    </div>
                    
                    {/* Bounce Backs */}
                    {streak.has_bounce_back && (
                      <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 p-2 rounded-lg">
                        <span>🔄</span>
                        <span>Bounce Back available!</span>
                      </div>
                    )}
                    
                    {/* Next milestone */}
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-xs text-gray-500">
                        {streak.current_streak < 7 && `${7 - streak.current_streak} days to Paper Chaser 📄`}
                        {streak.current_streak >= 7 && streak.current_streak < 30 && `${30 - streak.current_streak} days to Bag Getter 💰`}
                        {streak.current_streak >= 30 && streak.current_streak < 90 && `${90 - streak.current_streak} days to Heavy Hitter 🔥`}
                        {streak.current_streak >= 90 && streak.current_streak < 365 && `${365 - streak.current_streak} days to Mogul Status 👑`}
                        {streak.current_streak >= 365 && `You're a Mogul! 👑`}
                      </p>
                    </div>
                    
                    {/* Share button */}
                    {streak.current_streak >= 7 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          triggerMilestone({
                            streakDays: streak.current_streak,
                            title: streak.street_cred_title,
                            totalSaved: 0,
                            billsPaid: 0,
                          });
                        }}
                        className="mt-3 w-full py-2 bg-gradient-to-r from-[#CCFF00] to-[#9ACD32] text-black text-sm font-bold rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                      >
                        📤 Share Your Streak!
                      </button>
                    )}
                    </div>
                  </>
                )}
              </div>
              
              {/* Notification Bell */}
              <NotificationBell user={user} />
              
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
              <Link to="/app/profile" className="flex-shrink-0" data-testid="profile-avatar-link">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full hover:ring-2 hover:ring-blue-400 transition-all" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold hover:ring-2 hover:ring-blue-400 transition-all">
                    {user?.name?.charAt(0) || '?'}
                  </div>
                )}
              </Link>
              
              {/* Admin Link - Desktop only, for admin users */}
              {isAdmin && (
                <Link
                  to="/admin"
                  data-testid="admin-link"
                  className="hidden md:flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Shield className="w-4 h-4" />
                  Admin
                </Link>
              )}
              
              <button
                onClick={handleLogout}
                data-testid="logout-button"
                className="hidden md:block px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                Logout
              </button>
              
              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white">
            <div className="px-4 py-3 pb-32 max-h-[80vh] overflow-y-auto">
              {/* Core Section */}
              <div className="mb-4">
                <p className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  {mobileNavGroups.core.label}
                </p>
                <div className="space-y-1">
                  {mobileNavGroups.core.items.map((item) => {
                    const IconComponent = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsMenuOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                          location.pathname === item.path
                            ? 'bg-blue-50 text-blue-600'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <IconComponent className={`w-5 h-5 ${location.pathname === item.path ? 'text-blue-600' : 'text-gray-500'}`} />
                        <span className="font-medium">{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-100 my-3" />

              {/* Growth & Community Section */}
              <div className="mb-4">
                <p className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  {mobileNavGroups.growth.label}
                </p>
                <div className="space-y-1">
                  {mobileNavGroups.growth.items.map((item) => {
                    const IconComponent = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsMenuOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                          location.pathname === item.path
                            ? 'bg-blue-50 text-blue-600'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <IconComponent className={`w-5 h-5 ${location.pathname === item.path ? 'text-blue-600' : 'text-gray-500'}`} />
                        <span className="font-medium">{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-100 my-3" />

              {/* System Section */}
              <div className="mb-4">
                <p className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  {mobileNavGroups.system.label}
                </p>
                <div className="space-y-1">
                  {mobileNavGroups.system.items.map((item) => {
                    const IconComponent = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsMenuOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                          location.pathname === item.path
                            ? 'bg-blue-50 text-blue-600'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                        data-testid={item.path === '/app/profile' ? 'mobile-profile-link' : undefined}
                      >
                        <IconComponent className={`w-5 h-5 ${location.pathname === item.path ? 'text-blue-600' : 'text-gray-500'}`} />
                        <span className="font-medium">{item.label}</span>
                      </Link>
                    );
                  })}
                  
                  {/* Premium Upsell - Only show for free users */}
                  {!isSubscribed && (
                    <Link
                      to="/app/pricing"
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-amber-600 hover:bg-amber-50 transition-colors"
                      data-testid="mobile-premium-link"
                    >
                      <Sparkles className="w-5 h-5 text-amber-500" />
                      <span className="font-medium">GuapGoals Pro</span>
                    </Link>
                  )}
                  
                  {/* Admin Panel - Only show for admin users */}
                  {isAdmin && (
                    <Link
                      to="/admin"
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                      data-testid="mobile-admin-link"
                    >
                      <Shield className="w-5 h-5 text-red-500" />
                      <span className="font-medium">Admin Panel</span>
                    </Link>
                  )}
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-100 my-3" />

              {/* Secondary Links (Contact & About) */}
              <div className="px-3 py-2 flex items-center justify-center gap-2 text-sm">
                <Link
                  to="/app/contact"
                  onClick={() => setIsMenuOpen(false)}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Contact
                </Link>
                <span className="text-gray-300">•</span>
                <Link
                  to="/app/about"
                  onClick={() => setIsMenuOpen(false)}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  About
                </Link>
              </div>

              {/* User Profile Block */}
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 px-3 py-2.5 w-full text-left text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium">Logout</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Interactive Tutorial */}
      {showTutorial && !showWelcomeGuide && (
        <Tutorial onComplete={completeTutorial} />
      )}

      {/* Main Content */}
      <main id="main-content" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" role="main" tabIndex="-1">
        {children}
      </main>

      {/* Security Footer */}
      <footer className="border-t border-gray-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-4 text-sm text-gray-500">
            <Link to="/app/security" className="flex items-center gap-1 hover:text-gray-700">
              <span className="text-green-600">🔒</span>
              <span>Secured & Encrypted</span>
            </Link>
            <span className="hidden sm:inline">•</span>
            <Link to="/onboarding" className="text-emerald-600 hover:text-emerald-800 flex items-center gap-1">
              <span>🚀</span>
              <span>Guided Setup</span>
            </Link>
            <span className="hidden sm:inline">•</span>
            <button
              onClick={resetTutorial}
              className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <span>📚</span>
              <span>Quick Tour</span>
            </button>
            <span className="hidden sm:inline">•</span>
            <a
              href="https://forms.gle/d895nsCtERWW1pDt9"
              target="_blank"
              rel="noopener noreferrer"
              className="text-yellow-600 hover:text-yellow-700 flex items-center gap-1"
              data-testid="footer-feedback-btn"
            >
              <span>💬</span>
              <span>Give Feedback</span>
            </a>
          </div>
        </div>
      </footer>
      
      {/* Ash Chat Button - appears on all pages */}
      <AshChatButton />
      
      {/* Beta Tester Badge - Removed */}
      
      {/* Install App Prompt for mobile users */}
      <InstallPrompt />
      
      {/* Notification Permission Prompt (only shows when app is installed as PWA) */}
      {showNotificationPrompt && (
        <NotificationPrompt onClose={dismissNotificationPrompt} />
      )}
      
      {/* Login Notification Prompt (shows on every login for all users) */}
      {showLoginNotificationPrompt && user?.user_id && (
        <LoginNotificationPrompt 
          onClose={dismissLoginNotificationPrompt} 
          userId={user.user_id}
        />
      )}
      
      {/* Firebase Push Notification Prompt */}
      <PushNotificationPrompt />
      
      {/* Shareable Milestone Modal */}
      {showMilestone && milestoneData && (
        <ShareableMilestoneCard
          streakDays={milestoneData.streakDays}
          title={milestoneData.title}
          totalSaved={milestoneData.totalSaved}
          billsPaid={milestoneData.billsPaid}
          onClose={closeMilestone}
        />
      )}
      
      {/* Real-time Toast Notifications */}
      <RealtimeToastContainer />
    </div>
  );
}

export default Layout;